import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { PageHeader } from '@/components/layout/PageHeader'
import { useDocumentHead } from '@/hooks/use-document-head'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Slider } from '@/components/ui/slider'
import { SelectableBox } from '@/components/ui/selectable-box'
import { cn } from '@/lib/utils'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Card, CardContent } from '@/components/ui/card'
import {
  agentsApiClient,
  type AgentWithVoiceSettings,
} from '@/lib/api/agents.client'
import { toast } from 'sonner'
import { Skeleton } from '@/components/ui/skeleton'
import { useAppAuth } from '@/contexts/app-auth.context'
import {
  AGENT_TONE_VALUES,
  VOICE_SETTINGS_DEFAULTS,
  type AgentTone,
} from '@super/shared-types'
import { VoiceSearch } from '@/components/voice/VoiceSearch'
import { type VoiceOption } from '@/lib/api/elevenlabs.client'

const toneOptions = AGENT_TONE_VALUES

const llmOptions = [
  {
    id: 'gpt-4o-mini',
    label: 'GPT-4o Mini',
    description: 'OpenAI - fastest',
  },
  {
    id: 'gpt-4-turbo',
    label: 'GPT-4 Turbo',
    description: 'OpenAI - balanced',
  },
  {
    id: 'gpt-4o',
    label: 'GPT-4o',
    description: 'OpenAI - most capable',
  },
]

const sttOptions = [
  {
    id: 'whisper-1',
    label: 'Whisper',
    description: 'OpenAI',
  },
  {
    id: 'nova-3',
    label: 'Nova',
    description: 'Deepgram',
  },
]

const formSchema = z.object({
  name: z.string().min(1, 'Agent name is required'),
  disallowedTopics: z.string().optional(),
  tone: z.array(z.enum(AGENT_TONE_VALUES)).min(1, 'Select at least one tone'),
  temperature: z.number().min(0).max(1),
  greeting: z.string().optional(),
  voice: z.string().optional(),
  voiceModelId: z.string().optional(),
  voiceModelLLM: z.string().min(1, 'Select an LLM model'),
  voiceModelSTT: z.string().min(1, 'Select an STT model'),
})

type FormValues = z.infer<typeof formSchema>

const getTemperatureLabel = (value: number): string => {
  if (value === 0) return 'Precise'
  if (value === 0.25) return 'Consistent'
  if (value === 0.5) return 'Balanced'
  if (value === 0.75) return 'Creative'
  if (value === 1) return 'Experimental'
  return `${value}`
}

export function AgentSettingsPage() {
  useDocumentHead({
    title: 'Agent Settings',
    description: 'Configure your voice agent settings',
  })

  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [agent, setAgent] = useState<AgentWithVoiceSettings | null>(null)
  const [formData, setFormData] = useState<FormValues | null>(null)
  const [selectedVoice, setSelectedVoice] = useState<VoiceOption | null>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: VOICE_SETTINGS_DEFAULTS,
  })

  useEffect(() => {
    if (currentCompany?.id) {
      loadAgent()
    }
  }, [currentCompany?.id])

  // Watch for formData changes and reset form
  useEffect(() => {
    if (formData) {
      form.reset(formData)
    }
  }, [formData, form])

  const handleVoiceSelect = (voice: VoiceOption) => {
    setSelectedVoice(voice)
    form.setValue('voice', voice.name)
    form.setValue('voiceModelId', voice.id)
  }

  const loadAgent = async () => {
    if (!currentCompany?.id) {
      console.error('No current company available')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      // Super admin specifies companyId, company admin uses their own company
      const existingAgent = isSuperAdmin
        ? await agentsApiClient.getAgentForCompany(currentCompany.id)
        : await agentsApiClient.getCompanyAgent()

      if (existingAgent) {
        setAgent(existingAgent)
        const newFormData = {
          name: existingAgent.name,
          disallowedTopics:
            existingAgent.voiceSettings?.disallowedTopics ||
            VOICE_SETTINGS_DEFAULTS.disallowedTopics,
          tone:
            existingAgent.voiceSettings?.tone || VOICE_SETTINGS_DEFAULTS.tone,
          temperature:
            existingAgent.voiceSettings?.temperature ||
            VOICE_SETTINGS_DEFAULTS.temperature,
          greeting:
            existingAgent.voiceSettings?.greeting ||
            VOICE_SETTINGS_DEFAULTS.greeting,
          voice:
            existingAgent.voiceSettings?.voice || VOICE_SETTINGS_DEFAULTS.voice,
          voiceModelId: existingAgent.voiceSettings?.voiceModelId || undefined,
          voiceModelLLM:
            existingAgent.voiceSettings?.voiceModelLLM ||
            VOICE_SETTINGS_DEFAULTS.voiceModelLLM,
          voiceModelSTT:
            existingAgent.voiceSettings?.voiceModelSTT ||
            VOICE_SETTINGS_DEFAULTS.voiceModelSTT,
        }
        setFormData(newFormData)
        form.reset(newFormData)

        // If we have a voiceModelId, create a placeholder voice option
        // The actual voice data will be loaded when VoiceSearch component mounts
        if (
          existingAgent.voiceSettings?.voiceModelId &&
          existingAgent.voiceSettings?.voice
        ) {
          setSelectedVoice({
            id: existingAgent.voiceSettings.voiceModelId,
            name: existingAgent.voiceSettings.voice,
            accent: 'american',
            previewUrl: '',
            labels: {
              description: '',
              age: 'adult',
              gender: 'male',
              use_case: 'general',
            },
          })
        }
      } else {
        // No agent exists, use defaults
        setFormData(VOICE_SETTINGS_DEFAULTS)
        form.reset(VOICE_SETTINGS_DEFAULTS)
      }
    } catch (error) {
      console.error('Error loading agent:', error)
      toast.error('Failed to load agent settings')
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (values: FormValues) => {
    if (!currentCompany?.id) {
      toast.error('No current company selected')
      return
    }

    try {
      setSaving(true)

      const requestData = {
        name: values.name,
        voiceSettings: {
          disallowedTopics: values.disallowedTopics || undefined,
          tone: values.tone,
          temperature: values.temperature,
          greeting: values.greeting || undefined,
          voice: values.voice || undefined,
          voiceModelId: values.voiceModelId || undefined,
          voiceModelLLM: values.voiceModelLLM,
          voiceModelSTT: values.voiceModelSTT,
          voiceModelTTS: undefined, // Not implemented in UI yet
        },
      }

      if (agent) {
        // Update existing agent
        const updated = isSuperAdmin
          ? await agentsApiClient.updateAgentForCompany(agent.id, requestData)
          : await agentsApiClient.updateAgent(agent.id, requestData)
        setAgent(updated)
        toast.success('Agent settings updated successfully')
      } else {
        // Create new agent
        const created = isSuperAdmin
          ? await agentsApiClient.createAgentForCompany({
              ...requestData,
              companyId: currentCompany.id,
            })
          : await agentsApiClient.createAgent(requestData)
        setAgent(created)
        toast.success('Agent created successfully')
      }
    } catch (error) {
      console.error('Error saving agent:', error)
      toast.error('Failed to save agent settings')
    } finally {
      setSaving(false)
    }
  }

  // Show skeleton during loading or when form data is not ready
  if (loading || !formData) {
    return (
      <div className="space-y-6 p-6 pt-0">
        <PageHeader title="Configure your voice agent settings" />

        <Card>
          <CardContent className="pt-6 max-w-156">
            <div className="space-y-6">
              <div className="min-w-56">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-10 w-full max-w-96" />
              </div>
              <div className="min-w-56">
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-32 w-full" />
              </div>
              <div>
                <Skeleton className="h-4 w-16 mb-2" />
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-20" />
                  ))}
                </div>
              </div>
              <div className="min-w-56">
                <Skeleton className="h-4 w-28 mb-2" />
                <Skeleton className="h-24 w-full" />
              </div>
              <div className="min-w-56">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-full max-w-96" />
              </div>
              <div className="min-w-56">
                <Skeleton className="h-4 w-28 mb-2" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div>
                <Skeleton className="h-4 w-36 mb-2" />
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-20 w-32" />
                  ))}
                </div>
              </div>
              <div>
                <Skeleton className="h-4 w-40 mb-2" />
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <Skeleton key={i} className="h-20 w-32" />
                  ))}
                </div>
              </div>
              <Skeleton className="h-10 w-32" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6 pt-0">
      <PageHeader title="Configure your voice agent settings" />

      <Card>
        <CardContent className="pt-6 max-w-156">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="min-w-56">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="mb-8 max-w-96">
                      <FormLabel className="mb-2">Agent name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter the name of the voice agent"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="min-w-56">
                <FormField
                  control={form.control}
                  name="disallowedTopics"
                  render={({ field }) => (
                    <FormItem className="mb-8">
                      <FormLabel className="mb-2">Off-limit topics</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter topics the agent should avoid discussing"
                          className="min-h-[120px]"
                          rows={4}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="tone"
                render={({ field }) => (
                  <FormItem className="mb-8">
                    <FormLabel className="mb-2">Tone</FormLabel>
                    <FormControl>
                      <div className="flex flex-wrap gap-2">
                        {toneOptions.map(tone => (
                          <SelectableBox
                            key={tone}
                            id={tone}
                            selected={field.value.includes(tone)}
                            onSelect={id => {
                              const typedId = id as AgentTone
                              const newValue = field.value.includes(typedId)
                                ? field.value.filter(t => t !== typedId)
                                : [...field.value, typedId]
                              field.onChange(newValue)
                            }}
                          >
                            <span className="capitalize">{tone}</span>
                          </SelectableBox>
                        ))}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="min-w-56">
                <FormField
                  control={form.control}
                  name="greeting"
                  render={({ field }) => (
                    <FormItem className="mb-8">
                      <FormLabel className="mb-2">Voice greeting</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Hello! How can I help you today?"
                          className="min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="min-w-56">
                <FormItem className="mb-8 max-w-96">
                  <FormLabel className="mb-2">Voice Selection</FormLabel>
                  <FormControl>
                    <VoiceSearch
                      selectedVoice={selectedVoice}
                      onVoiceSelect={handleVoiceSelect}
                      placeholder="Search ElevenLabs voices..."
                      greetingText={
                        form.watch('greeting') ||
                        'Hello! How can I help you today?'
                      }
                    />
                  </FormControl>
                </FormItem>
              </div>

              <div className="min-w-56">
                <FormField
                  control={form.control}
                  name="temperature"
                  render={({ field }) => (
                    <FormItem className="mb-8 max-w-96">
                      <FormLabel className="mb-2">Temperature</FormLabel>
                      <FormControl>
                        <Slider
                          min={0}
                          max={1}
                          step={0.25}
                          value={[field.value]}
                          onValueChange={value => field.onChange(value[0])}
                          getTooltipLabel={getTemperatureLabel}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="voiceModelLLM"
                render={({ field }) => (
                  <FormItem className="mb-8">
                    <FormLabel className="mb-2">Language Model (LLM)</FormLabel>
                    <FormControl>
                      <div className="flex flex-wrap gap-2">
                        {llmOptions.map(option => (
                          <SelectableBox
                            key={option.id}
                            id={option.id}
                            selected={field.value === option.id}
                            onSelect={id => field.onChange(id)}
                          >
                            <div className="space-y-0.5">
                              <div className="font-medium">{option.label}</div>
                              <div
                                className={cn(
                                  'text-xs',
                                  field.value === option.id
                                    ? 'text-white/80'
                                    : 'text-[#757575]',
                                )}
                              >
                                {option.description}
                              </div>
                            </div>
                          </SelectableBox>
                        ))}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="voiceModelSTT"
                render={({ field }) => (
                  <FormItem className="mb-8">
                    <FormLabel className="mb-2">
                      Speech-to-Text Model (STT)
                    </FormLabel>
                    <FormControl>
                      <div className="flex flex-wrap gap-2">
                        {sttOptions.map(option => (
                          <SelectableBox
                            key={option.id}
                            id={option.id}
                            selected={field.value === option.id}
                            onSelect={id => field.onChange(id)}
                          >
                            <div className="space-y-0.5">
                              <div className="font-medium">{option.label}</div>
                              <div
                                className={cn(
                                  'text-xs',
                                  field.value === option.id
                                    ? 'text-white/80'
                                    : 'text-[#757575]',
                                )}
                              >
                                {option.description}
                              </div>
                            </div>
                          </SelectableBox>
                        ))}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-start mt-12">
                <Button type="submit" disabled={saving} variant="form">
                  {saving
                    ? 'Saving...'
                    : agent
                      ? 'Update Agent'
                      : 'Create Agent'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
